// Performance monitoring utilities

// Core Web Vitals tracking
export const trackWebVitals = (onPerfEntry) => {
  if (onPerfEntry && onPerfEntry instanceof Function) {
    import('web-vitals').then(({ getCLS, getFID, getFCP, getLCP, getTTFB }) => {
      getCLS(onPerfEntry);
      getFID(onPerfEntry);
      getFCP(onPerfEntry);
      getLCP(onPerfEntry);
      getTTFB(onPerfEntry);
    });
  }
};

// Image lazy loading observer
export const createImageObserver = (callback) => {
  if ('IntersectionObserver' in window) {
    return new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            callback(entry.target);
          }
        });
      },
      {
        rootMargin: '50px 0px',
        threshold: 0.01
      }
    );
  }
  return null;
};

// Preload critical resources
export const preloadCriticalResources = () => {
  const criticalImages = [
    '/img/hero-bg.jpg',
    '/img/logo.png'
  ];

  criticalImages.forEach((src) => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = src;
    document.head.appendChild(link);
  });
};

// Debounce function for performance optimization
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Throttle function for scroll events
export const throttle = (func, limit) => {
  let inThrottle;
  return function() {
    const args = arguments;
    const context = this;
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

// Memory usage monitoring
export const monitorMemoryUsage = () => {
  if ('memory' in performance) {
    const memory = performance.memory;
    console.log('Memory Usage:', {
      used: Math.round(memory.usedJSHeapSize / 1048576) + ' MB',
      total: Math.round(memory.totalJSHeapSize / 1048576) + ' MB',
      limit: Math.round(memory.jsHeapSizeLimit / 1048576) + ' MB'
    });
  }
};

// Resource timing analysis
export const analyzeResourceTiming = () => {
  const resources = performance.getEntriesByType('resource');
  const analysis = {
    totalResources: resources.length,
    slowResources: [],
    largeResources: []
  };

  resources.forEach((resource) => {
    const duration = resource.responseEnd - resource.requestStart;
    const size = resource.transferSize || 0;

    if (duration > 1000) { // Slow resources (>1s)
      analysis.slowResources.push({
        name: resource.name,
        duration: Math.round(duration),
        size: Math.round(size / 1024) + ' KB'
      });
    }

    if (size > 100000) { // Large resources (>100KB)
      analysis.largeResources.push({
        name: resource.name,
        size: Math.round(size / 1024) + ' KB',
        duration: Math.round(duration)
      });
    }
  });

  return analysis;
};

// Performance budget checker
export const checkPerformanceBudget = () => {
  const budget = {
    maxLoadTime: 3000, // 3 seconds
    maxImageSize: 500000, // 500KB
    maxJSSize: 1000000, // 1MB
    maxCSSSize: 200000 // 200KB
  };

  const navigation = performance.getEntriesByType('navigation')[0];
  const loadTime = navigation.loadEventEnd - navigation.navigationStart;

  const violations = [];

  if (loadTime > budget.maxLoadTime) {
    violations.push(`Load time exceeded: ${Math.round(loadTime)}ms > ${budget.maxLoadTime}ms`);
  }

  const resources = performance.getEntriesByType('resource');
  resources.forEach((resource) => {
    const size = resource.transferSize || 0;
    const name = resource.name;

    if (name.match(/\.(jpg|jpeg|png|gif|webp)$/i) && size > budget.maxImageSize) {
      violations.push(`Image size exceeded: ${name} (${Math.round(size / 1024)}KB)`);
    }

    if (name.match(/\.js$/i) && size > budget.maxJSSize) {
      violations.push(`JS size exceeded: ${name} (${Math.round(size / 1024)}KB)`);
    }

    if (name.match(/\.css$/i) && size > budget.maxCSSSize) {
      violations.push(`CSS size exceeded: ${name} (${Math.round(size / 1024)}KB)`);
    }
  });

  return violations;
};